import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs, generateBlogPostSchema } from '../../utils/seo';

const AISoapNotes: React.FC = () => {
  // Generate structured data for the blog post
  const blogSchema = generateBlogPostSchema({
    title: "AI for SOAP Notes: Everything a PT Should Know in 2025",
    description: "Complete guide to AI documentation tools for physical therapists. Learn about benefits, risks, and what to look for in AI SOAP note solutions.",
    datePublished: "2025-01-15",
    dateModified: "2025-01-15",
    author: "Fownd Team",
    url: "/blogs/ai-soap-notes-pt-guide-2025",
    image: "/blog/default.jpg"
  });

  return (
    <>
      <MetaTags
        title={pageConfigs.blogAISoapNotes.title}
        description={pageConfigs.blogAISoapNotes.description}
        keywords={pageConfigs.blogAISoapNotes.keywords}
        canonical={pageConfigs.blogAISoapNotes.canonical}
        ogImage="/blog/default.jpg"
        ogType="article"
        schemaData={blogSchema}
        additionalMeta={[
          { property: "article:author", content: "Fownd Team" },
          { property: "article:published_time", content: "2025-01-15T00:00:00Z" },
          { property: "article:modified_time", content: "2025-01-15T00:00:00Z" },
          { property: "article:section", content: "Healthcare Technology" },
          { property: "article:tag", content: "AI SOAP Notes" },
          { property: "article:tag", content: "Physical Therapy Documentation" },
          { property: "article:tag", content: "Healthcare AI" },
          { property: "article:tag", content: "Clinical Documentation" },
          { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" }
        ]}
      />
      
      <div className="bg-white">
        {/* Hero section with title */}
        <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center px-4">
              <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
                AI for SOAP Notes: Everything a PT Should Know in 2025
              </h1>
              <p className="text-emerald-100 text-lg">
                A comprehensive guide to AI documentation tools and what physical therapists need to know
              </p>
              <p className="text-emerald-200 text-sm mt-4">
                By Fownd Team • January 15, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Blog content */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {/* Back button at top */}
          <div className="mb-8">
            <Link 
              to="/blogs" 
              className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blogs
            </Link>
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              SOAP notes are the backbone of physical therapy documentation. They are essential for continuity of care, compliance, and reimbursement. They are also one of the biggest sources of administrative burden for clinicians. Many PTs report spending an extra one to two hours each day completing notes after patients have left.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              AI is beginning to reshape how SOAP notes are written, reviewed, and used. In 2025, PTs now have a range of tools to choose from, but not all of them are designed with rehabilitation in mind. This guide explains what AI for SOAP notes really means, the benefits and risks, and what PTs should look for in a documentation partner.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Are SOAP Notes and Why They Matter</h2>

            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Subjective:</h3>
                  <p className="text-gray-700">The patient's perspective — including their main complaint, pain details, functional limitations, relevant history, and personal goals.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Objective:</h3>
                  <p className="text-gray-700">The clinician's measurable findings — such as range of motion, strength, balance, special tests, and outcome scores.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Assessment:</h3>
                  <p className="text-gray-700">The therapist's clinical reasoning — progress toward goals, barriers, and professional interpretation of the findings.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Plan:</h3>
                  <p className="text-gray-700">The roadmap for care — planned interventions, home exercise program, visit frequency, and next steps.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-8">
              For PTs, SOAP notes are more than a template. They are the record that protects your care. They standardize documentation, demonstrate progress toward goals, and provide the compliance needed for reimbursement. Done well, they highlight the expertise behind every treatment decision.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How AI Is Being Used for SOAP Notes</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              AI documentation tools vary widely in what they can do. Most start with transcription and summarization, while more advanced platforms are beginning to support structured data capture, coding suggestions, and even real-time drafting. Together, these are the main ways AI is being applied to SOAP notes today:
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <ul className="space-y-3 text-gray-700">
                <li><strong>Transcription:</strong> Many AI scribes convert therapist–patient dialogue into draft notes.</li>
                <li><strong>Summarization:</strong> Some tools extract key points from a visit and format them into SOAP sections.</li>
                <li><strong>Structured Capture:</strong> A smaller group of solutions can organize observations into PT-specific fields such as ROM, strength, or outcome scores.</li>
                <li><strong>Coding Support:</strong> Emerging tools suggest CPT and ICD-10 codes based on the content of the note.</li>
                <li><strong>Real-Time Drafting:</strong> The most advanced systems aim to generate SOAP notes during the session so they are ready by the end of care.</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Benefits of AI for SOAP Notes</h2>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Efficiency</h3>
                <p className="text-green-700">Reduces hours of after-care charting.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Accuracy</h3>
                <p className="text-green-700">Standardizes language, reduces missed details.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Consistency</h3>
                <p className="text-green-700">Keeps notes compliant and audit-ready.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Engagement</h3>
                <p className="text-green-700">Lets PTs focus more on patients and less on typing.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6 md:col-span-2">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Scalability</h3>
                <p className="text-green-700">Helps clinics maintain documentation quality across teams.</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Pitfalls and Limitations to Watch For</h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <ul className="space-y-3 text-gray-700">
                <li><strong>Generic AI scribes:</strong> May not understand PT-specific terms, tests, or outcomes.</li>
                <li><strong>Editing overhead:</strong> Some drafts take longer to fix than to write manually.</li>
                <li><strong>Compliance risks:</strong> Not all AI tools are HIPAA compliant.</li>
                <li><strong>Loss of nuance:</strong> AI can sometimes miss the therapist's clinical reasoning, making notes too generic.</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What PTs Should Look For in 2025</h2>

            <div className="bg-emerald-50 rounded-lg p-6 mb-8">
              <ul className="space-y-4 text-gray-700">
                <li>
                  <strong className="text-emerald-800">PT-specific design:</strong> Does it recognize therapy workflows like gait analysis, joint mobility, or outcome measures?
                </li>
                <li>
                  <strong className="text-emerald-800">Real-time workflow:</strong> Can it keep up during sessions, or only summarize afterward?
                </li>
                <li>
                  <strong className="text-emerald-800">HIPAA compliance:</strong> Is PHI protected? Will the vendor provide a BAA?
                </li>
                <li>
                  <strong className="text-emerald-800">Clinician-in-the-loop control:</strong> Does the PT remain the final author of the note?
                </li>
                <li>
                  <strong className="text-emerald-800">Integration flexibility:</strong> Can notes move easily into your EMR without forcing a full switch?
                </li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Closing</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              AI for SOAP notes is no longer futuristic. It is here. In 2025, PTs have real choices: general scribes, EMR add-ons, or purpose-built AI assistants. The right solution should make documentation faster, easier, and more compliant. It should also reflect the clinical expertise PTs bring to patient care.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              The bottom line: AI will not replace SOAP notes. It will change how they are written. PTs who adopt the right tools will spend less time documenting and more time doing what they do best: treating patients.
            </p>

            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-6 border border-emerald-100 mt-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Documentation is changing fast, and PTs deserve tools built for their workflows. That's why we're building Notation at Fownd.
              </p>
            </div>
          </article>

          {/* Try Notation CTA Banner */}
          <div className="mt-16 p-8 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Experience AI Documentation Built for PTs?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                👉 Try Notation for your next evaluation — no strings attached.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center justify-center"
                >
                  Try Notation
                </a>
                <Link
                  to="/demo"
                  className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold border-2 border-emerald-600 hover:bg-emerald-50 transition-colors inline-flex items-center justify-center"
                >
                  Watch Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AISoapNotes;
